import React from "react"
import { useDispatch } from "react-redux"
import { CustomInput, Input } from "reactstrap"
import { v4 } from "uuid"
import { FightPlayerItem } from 'web/containers/shared/FightPlayerItem'
import { GuiData } from 'web/data/gui-data'
import { EditSetupActions } from "web/redux/slices/edit-setup-slice"
import { ModalActions } from "web/redux/slices/modal-slice"
import { createGroupedOptions } from "web/utils/jsx-utils"
import { FromRosterSelectBox } from './FromRosterSelectBox'
import { getCurrentBuild } from 'web/data/core-data'
import { sortPlayers } from 'web/utils/sort-utils'
import { FightSetup, FullFight } from 'web/data/models'
import { useRosterState } from 'web/redux/slices/roster-slice'

type Props = {
    fullFight: FullFight
    setup: FightSetup

    setupNameInput: {
        name: string
        setSetupName: (e: React.ChangeEvent<HTMLInputElement>) => void
        setSetupNameOnBlur: (e: React.ChangeEvent<HTMLInputElement>) => void
    }
}

export const EditSetupSetupSidebar: React.FC<Props> = ({
    fullFight,
    setup,
    setupNameInput
}) => {

    const disp = useDispatch()
    const buildData = getCurrentBuild()

    const rosterState = useRosterState()
    const rosterPlayerNames = [...new Set(rosterState.players.map(player => player.name))]
        .sort()

    const createCustomPlayer = (customPlayerSpec: string) => {
        disp(ModalActions.openEditPlayerModal({
            mode: "setup-add-custom",
            rosterPlayerNames: rosterPlayerNames,
            player: {
                id: v4(),
                name: '',
                specId: customPlayerSpec,
                talentIds: [],
                modifierIds: [],
                overrides: [],
                patchInfo: buildData.patchInfo,
            },
            title: "Create custom player",
            saveBtnText: "Save new player",
            onConfirm: (player) => disp(EditSetupActions.addPlayer({ player }))
        }))
    }

    const fightPlayers = [...setup.fightPlayers].sort(sortPlayers)

    // const [s, setS] = useState(false)

    return (
        <div
            className="h-100 d-flex flex-column space-my-3"
            style={{ width: "282px" }}
        >

            {/* Setup name STARTS */}
            <div className="border-bottom d-flex flex-column mt-n3 mr-n3 ml-n3 p-3 space-my-3">
                <div className="d-flex flex-column space-my-2">
                    <label className="small font-weight-bold">
                        {"Setup name"}
                    </label>
                    <div className="d-flex space-mx-2">
                        <Input
                            bsSize="sm"
                            className="flex-grow-1"
                            value={setupNameInput.name}
                            onChange={setupNameInput.setSetupName}
                            onBlur={setupNameInput.setSetupNameOnBlur}
                        />
                        {/* <ToggleButton
                            tooltipText='Hello there'
                            checked={s}
                            onChecked={setS}
                        >
                            <SvgPatch />
                        </ToggleButton> */}
                    </div>
                </div>
            </div>
            {/* Setup name ENDS */}

            {/* Add players box STARTS */}
            <div className="bg-white border-bottom d-flex flex-column mt-n3 mr-n3 ml-n3 p-3 space-my-3">
                <div className="d-flex flex-column space-my-2">
                    <label className="small font-weight-bold">
                        {"Add from roster"}
                    </label>
                    <FromRosterSelectBox />
                </div>

                <div className="d-flex flex-column space-my-2">
                    <label className="small font-weight-bold">
                        {"Create player"}
                    </label>
                    <div className="d-flex space-mx-2 align-items-end">
                        <CustomInput
                            id={"custom-player-spec-select"}
                            bsSize="sm"
                            type="select"
                            // This is not a real select list, we trigger on select
                            value={''}
                            onChange={(e) => {
                                if (e.target.value !== "") {
                                    createCustomPlayer(e.target.value)
                                }
                            }}
                        >
                            <option value="">{"-- Select spec --"}</option>
                            {createGroupedOptions({ optionGroups: GuiData.PlayerSpecOptions })}
                        </CustomInput>
                    </div>
                </div>
            </div>
            {/* Add players box ENDS */}

            <div className="d-flex flex-column space-my-2 pb-3">

                {fightPlayers.length <= 0 &&
                    <div className="d-flex small text-muted p-3 text-center align-items-center font-italic">
                        {"Add some players to start planning your assignments"}
                    </div>
                }

                {fightPlayers.length >= 1 &&
                    fightPlayers.map(player => {
                        return (
                            <FightPlayerItem
                                key={player.id}
                                interactive
                                fightPlayer={player}
                                setup={setup}
                                fullFight={fullFight}

                                onEditPlayer={() => {
                                    disp(ModalActions.openEditPlayerModal({
                                        mode: "setup-edit",
                                        player,
                                        rosterPlayerNames,
                                        title: "Edit player",
                                        saveBtnText: "Save edits",
                                        onConfirm: (player) => {
                                            disp(EditSetupActions
                                                .updatePlayer({ player }))
                                        }
                                    }))
                                }}

                                onRemovePlayer={() =>
                                    disp(EditSetupActions
                                        .removePlayer({ playerId: player.id }))}
                            />
                        )
                    })
                }
            </div>

        </div>
    )
}
